import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Box,
  Checkbox,
  Menu,
  MenuItem,
  type SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  type Theme,
} from "@mui/material";
import {
  type Column,
  type ColumnDef,
  type ColumnSizingState,
  flexRender,
  type Row,
  type Table as TanstackTable,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import React, { useCallback, useRef, useState } from "react";

const DRAG_HANDLE_OFFSET = 20;

/** Extra px on table minWidth so horizontal scroll isn’t short of the true width (scrollbar/rounding). */
const TABLE_MIN_WIDTH_BUFFER_PX = 12;

function effectiveResizedColumnPixelWidth(
  col: { id: string; getSize: () => number; columnDef: { minSize?: number } },
  columnSizing: ColumnSizingState,
): number {
  const raw = col.getSize();
  const minW = col.columnDef.minSize;
  if (col.id in columnSizing && minW != null) {
    return Math.max(raw, minW);
  }
  return raw;
}

export type DataTableColumnMeta = {
  flex?: number;
  align?: "left" | "center" | "right";
  draggable?: boolean;
  compact?: boolean;
  frozen?: boolean;
};

declare module "@tanstack/react-table" {
  interface ColumnMeta<TData, TValue> extends DataTableColumnMeta {}
}

export function buildCheckboxColumn<T>(options?: {
  shiftSelectRef?: React.MutableRefObject<string | null>;
  onBulkSelect?: () => void;
}): ColumnDef<T, unknown> {
  return {
    id: "_select",
    header: ({ table }) => (
      <Checkbox
        size="small"
        checked={table.getIsAllRowsSelected()}
        indeterminate={table.getIsSomeRowsSelected()}
        onChange={(e) => {
          table.getToggleAllRowsSelectedHandler()(e);
          options?.onBulkSelect?.();
        }}
      />
    ),
    cell: ({ row, table }) => (
      <Checkbox
        size="small"
        checked={row.getIsSelected()}
        indeterminate={row.getIsSomeSelected()}
        onClick={(e) => {
          e.stopPropagation();

          const ref = options?.shiftSelectRef;

          if (ref && e.shiftKey && ref.current !== null) {
            const rows = table.getRowModel().rows;
            const lastIndex = rows.findIndex((r) => r.id === ref.current);
            const currentIndex = rows.findIndex((r) => r.id === row.id);

            if (lastIndex !== -1 && currentIndex !== -1) {
              const start = Math.min(lastIndex, currentIndex);
              const end = Math.max(lastIndex, currentIndex);

              table.setRowSelection((prev) => {
                const next = { ...prev };
                for (let i = start; i <= end; i++) {
                  next[rows[i].id] = true;
                }
                return next;
              });
            }
            return;
          }

          row.toggleSelected();
          if (ref) {
            ref.current = row.id;
          }
        }}
      />
    ),
    meta: { flex: 0, compact: true, frozen: true },
    size: 36,
    enableSorting: false,
    enableGlobalFilter: false,
    enableResizing: false,
    enableHiding: false,
  };
}

type DataTableProps<T> = {
  table: TanstackTable<T>;
  onRowClick?: (row: T) => void;
  onRowContextMenu?: (row: T, event: React.MouseEvent) => void;
  getRowClassName?: (row: T) => string;
  /** When set, controls cursor per row; otherwise rows use `pointer` whenever `onRowClick` is defined. */
  getRowCursor?: (row: T) => string;
  noRowsContent?: React.ReactNode;
  pagination?: boolean;
  pageSizeOptions?: number[];
  toolbar?: React.ReactNode;
  rowHeight?: number;
  headerHeight?: number;
  virtualize?: boolean;
  enableColumnHiding?: boolean;
  onHideColumn?: (columnId: string) => void;
  sx?: SxProps<Theme>;
};

const MemoizedRow = React.memo(function MemoizedRow<T>({
  row,
  onClick,
  onContextMenu,
  className,
  rowHeight,
  cursor,
}: {
  row: Row<T>;
  onClick?: () => void;
  onContextMenu?: (e: React.MouseEvent) => void;
  className: string;
  rowHeight: number;
  cursor: string;
}) {
  const cells = row.getVisibleCells();
  const columnSizing = cells[0]?.getContext().table.getState().columnSizing ?? {};

  const frozenOffsets: Record<string, number> = {};
  let frozenLeft = 0;
  for (const cell of cells) {
    const meta = cell.column.columnDef.meta as DataTableColumnMeta | undefined;
    if (!meta?.frozen) break;
    const col = cell.column;
    frozenOffsets[col.id] = frozenLeft;
    frozenLeft += effectiveResizedColumnPixelWidth(col, columnSizing);
  }

  return (
    <TableRow
      hover
      className={className}
      onClick={onClick}
      onContextMenu={onContextMenu}
      sx={{ cursor, height: rowHeight }}
    >
      {cells.map((cell) => {
        const meta = cell.column.columnDef.meta as DataTableColumnMeta | undefined;
        const isFrozen = meta?.frozen === true;
        return (
          <TableCell
            key={cell.id}
            align={meta?.align ?? "left"}
            className={isFrozen ? "frozen-column" : undefined}
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              ...(meta?.compact && { px: 0.25 }),
              ...(isFrozen && {
                position: "sticky",
                left: frozenOffsets[cell.column.id],
                zIndex: 1,
              }),
            }}
          >
            {meta?.draggable ? (
              <Box sx={{ ml: `${DRAG_HANDLE_OFFSET}px`, overflow: "hidden", textOverflow: "ellipsis" }}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </Box>
            ) : (
              flexRender(cell.column.columnDef.cell, cell.getContext())
            )}
          </TableCell>
        );
      })}
    </TableRow>
  );
}) as <T>(props: {
  row: Row<T>;
  onClick?: () => void;
  onContextMenu?: (e: React.MouseEvent) => void;
  className: string;
  rowHeight: number;
  cursor: string;
}) => React.JSX.Element;

function VirtualizedBody<T>({
  rows,
  rowHeight,
  onRowClick,
  onRowContextMenu,
  getRowClassName,
  getRowCursor,
  scrollRef,
}: {
  rows: Row<T>[];
  rowHeight: number;
  onRowClick?: (row: T) => void;
  onRowContextMenu?: (row: T, event: React.MouseEvent) => void;
  getRowClassName?: (row: T) => string;
  getRowCursor?: (row: T) => string;
  scrollRef: React.RefObject<HTMLDivElement | null>;
}) {
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => rowHeight,
    overscan: 15,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();
  const defaultCursor = onRowClick ? "pointer" : "default";

  return (
    <TableBody>
      {virtualItems.length > 0 && <tr style={{ height: virtualItems[0].start }} />}
      {virtualItems.map((virtualItem) => {
        const row = rows[virtualItem.index];
        const className = getRowClassName?.(row.original) ?? "";
        const cursor = getRowCursor ? getRowCursor(row.original) : defaultCursor;
        return (
          <MemoizedRow
            key={row.id}
            row={row}
            onClick={onRowClick ? () => onRowClick(row.original) : undefined}
            onContextMenu={
              onRowContextMenu
                ? (e) => {
                    e.preventDefault();
                    onRowContextMenu(row.original, e);
                  }
                : undefined
            }
            className={className}
            rowHeight={rowHeight}
            cursor={cursor}
          />
        );
      })}
      {virtualItems.length > 0 && (
        <tr
          style={{
            height: totalSize - (virtualItems[virtualItems.length - 1]?.end ?? 0),
          }}
        />
      )}
    </TableBody>
  );
}

function PlainBody<T>({
  rows,
  rowHeight,
  onRowClick,
  onRowContextMenu,
  getRowClassName,
  getRowCursor,
}: {
  rows: Row<T>[];
  rowHeight: number;
  onRowClick?: (row: T) => void;
  onRowContextMenu?: (row: T, event: React.MouseEvent) => void;
  getRowClassName?: (row: T) => string;
  getRowCursor?: (row: T) => string;
}) {
  const defaultCursor = onRowClick ? "pointer" : "default";

  return (
    <TableBody>
      {rows.map((row) => {
        const className = getRowClassName?.(row.original) ?? "";
        const cursor = getRowCursor ? getRowCursor(row.original) : defaultCursor;
        return (
          <MemoizedRow
            key={row.id}
            row={row}
            onClick={onRowClick ? () => onRowClick(row.original) : undefined}
            onContextMenu={
              onRowContextMenu
                ? (e) => {
                    e.preventDefault();
                    onRowContextMenu(row.original, e);
                  }
                : undefined
            }
            className={className}
            rowHeight={rowHeight}
            cursor={cursor}
          />
        );
      })}
    </TableBody>
  );
}

function HeaderContextMenu<T>({
  column,
  anchorPosition,
  onClose,
  onHideColumn,
}: {
  column: Column<T> | null;
  anchorPosition: { top: number; left: number } | undefined;
  onClose: () => void;
  onHideColumn?: (columnId: string) => void;
}) {
  if (!column) return null;

  return (
    <Menu
      open={anchorPosition != null}
      onClose={onClose}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition}
    >
      <MenuItem
        onClick={() => {
          onHideColumn?.(column.id);
          column.toggleVisibility(false);
          onClose();
        }}
        sx={{ display: "flex", gap: 1, alignItems: "center", fontSize: "0.875rem" }}
      >
        <VisibilityOffIcon sx={{ fontSize: 18 }} />
        Hide column
      </MenuItem>
    </Menu>
  );
}

function DraggableTableHead<T>({
  table,
  headerHeight,
  columnSizing,
  hidingEnabled,
  onHideColumn,
}: {
  table: TanstackTable<T>;
  headerHeight: number;
  columnSizing: ColumnSizingState;
  hidingEnabled: boolean;
  onHideColumn?: (columnId: string) => void;
}) {
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [contextMenuPosition, setContextMenuPosition] = useState<{ top: number; left: number } | undefined>();
  const [contextMenuColumn, setContextMenuColumn] = useState<Column<T> | null>(null);
  const resizable = table.options.enableColumnResizing === true;

  const handleDrop = (targetId: string) => {
    if (!draggedId || draggedId === targetId) return;
    const currentOrder =
      table.getState().columnOrder.length > 0
        ? [...table.getState().columnOrder]
        : table.getAllLeafColumns().map((c) => c.id);
    const fromIdx = currentOrder.indexOf(draggedId);
    const toIdx = currentOrder.indexOf(targetId);
    if (fromIdx === -1 || toIdx === -1) return;
    currentOrder.splice(fromIdx, 1);
    currentOrder.splice(toIdx, 0, draggedId);
    table.setColumnOrder(currentOrder);
    setDraggedId(null);
    setDragOverId(null);
    setActiveDragId(null);
  };

  const handleHeaderContextMenu = useCallback(
    (e: React.MouseEvent, column: Column<T>) => {
      if (!hidingEnabled || !column.getCanHide()) return;
      e.preventDefault();
      setContextMenuPosition({ top: e.clientY - 6, left: e.clientX + 2 });
      setContextMenuColumn(column);
    },
    [hidingEnabled],
  );

  const handleContextMenuClose = useCallback(() => {
    setContextMenuPosition(undefined);
    setContextMenuColumn(null);
  }, []);

  return (
    <TableHead>
      {table.getHeaderGroups().map((headerGroup) => {
        const frozenOffsets: Record<string, number> = {};
        let frozenLeft = 0;
        for (const header of headerGroup.headers) {
          const m = header.column.columnDef.meta as DataTableColumnMeta | undefined;
          if (!m?.frozen) break;
          const col = header.column;
          frozenOffsets[col.id] = frozenLeft;
          frozenLeft += effectiveResizedColumnPixelWidth(col, columnSizing);
        }

        return (
          <TableRow key={headerGroup.id} sx={{ height: headerHeight }}>
            {headerGroup.headers.map((header) => {
              const meta = header.column.columnDef.meta as DataTableColumnMeta | undefined;
              const isDraggable = meta?.draggable === true;
              const isFrozen = meta?.frozen === true;
              const colId = header.column.id;
              const isDragged = draggedId === colId;
              const isOver = dragOverId === colId && draggedId !== colId;
              const isActiveGrab = activeDragId === colId;
              const canResize = resizable && header.column.getCanResize();
              const isResized = colId in columnSizing;
              const resizedHeaderWidth = isResized
                ? effectiveResizedColumnPixelWidth(header.column, columnSizing)
                : null;
              return (
                <TableCell
                  key={header.id}
                  align={meta?.align ?? "left"}
                  className={isFrozen ? "frozen-column" : undefined}
                  onContextMenu={
                    hidingEnabled && header.column.getCanHide()
                      ? (e) => handleHeaderContextMenu(e, header.column)
                      : undefined
                  }
                  onDragOver={
                    isDraggable
                      ? (e) => {
                          if (!draggedId || draggedId === colId) return;
                          e.preventDefault();
                          setDragOverId(colId);
                        }
                      : undefined
                  }
                  onDragLeave={isDraggable ? () => setDragOverId(null) : undefined}
                  onDrop={
                    isDraggable
                      ? (e) => {
                          e.preventDefault();
                          handleDrop(colId);
                        }
                      : undefined
                  }
                  onDragEnd={() => {
                    setDraggedId(null);
                    setDragOverId(null);
                    setActiveDragId(null);
                  }}
                  sx={{
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    opacity: isDragged ? 0.4 : 1,
                    bgcolor: isActiveGrab ? "var(--color-brand-primary-500)" : isOver ? "rgb(82, 82, 91)" : undefined,
                    transition: "background-color 150ms ease",
                    position: "sticky",
                    top: 0,
                    zIndex: 2,
                    ...(isResized && resizedHeaderWidth != null ? { width: `${resizedHeaderWidth}px` } : {}),
                    ...(meta?.compact && { px: 0.25 }),
                    ...(isFrozen && {
                      left: frozenOffsets[colId],
                      zIndex: 3,
                    }),
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {isDraggable && (
                      <Box
                        component="span"
                        draggable
                        onDragStart={(e) => {
                          const cell = (e.currentTarget as HTMLElement).closest("th");
                          if (cell) {
                            e.dataTransfer.setDragImage(cell, cell.offsetWidth / 2, cell.offsetHeight / 2);
                          }
                          setDraggedId(colId);
                          setActiveDragId(colId);
                          e.dataTransfer.effectAllowed = "move";
                        }}
                        onDragEnd={() => {
                          setActiveDragId(null);
                        }}
                        onMouseDown={() => setActiveDragId(colId)}
                        onMouseUp={() => setActiveDragId(null)}
                        sx={{
                          cursor: "grab",
                          display: "inline-flex",
                          color: "rgb(161, 161, 170)",
                          "&:hover": { color: "white" },
                          "&:active": { cursor: "grabbing" },
                        }}
                      >
                        <DragIndicatorIcon sx={{ fontSize: 16 }} />
                      </Box>
                    )}
                    {header.isPlaceholder ? null : header.column.getCanSort() ? (
                      <TableSortLabel
                        active={header.column.getIsSorted() !== false}
                        direction={header.column.getIsSorted() === "desc" ? "desc" : "asc"}
                        onClick={header.column.getToggleSortingHandler()}
                        sx={{ color: "inherit", "& .MuiTableSortLabel-icon": { color: "inherit" } }}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </TableSortLabel>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </Box>
                  {canResize && (
                    <Box
                      onMouseDown={header.getResizeHandler()}
                      onTouchStart={header.getResizeHandler()}
                      onDoubleClick={() => header.column.resetSize()}
                      sx={{
                        position: "absolute",
                        right: 0,
                        top: 0,
                        height: "100%",
                        width: "4px",
                        cursor: "col-resize",
                        userSelect: "none",
                        touchAction: "none",
                        bgcolor: header.column.getIsResizing() ? "rgb(161, 161, 170)" : "transparent",
                        "&:hover": { bgcolor: "rgb(161, 161, 170)" },
                      }}
                    />
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        );
      })}
      {hidingEnabled && (
        <HeaderContextMenu
          column={contextMenuColumn}
          anchorPosition={contextMenuPosition}
          onClose={handleContextMenuClose}
          onHideColumn={onHideColumn}
        />
      )}
    </TableHead>
  );
}

export default function DataTable<T>({
  table,
  onRowClick,
  onRowContextMenu,
  getRowClassName,
  getRowCursor,
  noRowsContent = "No Results",
  pagination = true,
  pageSizeOptions = [20, 60, 100],
  toolbar,
  rowHeight = 64,
  headerHeight = 70,
  virtualize = false,
  enableColumnHiding = false,
  onHideColumn,
  sx,
}: DataTableProps<T>) {
  const rows = table.getRowModel().rows;
  const scrollRef = useRef<HTMLDivElement>(null);
  const columnSizing = table.getState().columnSizing;
  const leafHeaders = table.getHeaderGroups().at(-1)?.headers ?? [];

  const minTableWidth =
    TABLE_MIN_WIDTH_BUFFER_PX +
    leafHeaders.reduce((sum, header) => {
      const col = header.column;
      return sum + (col.columnDef.minSize ?? col.getSize());
    }, 0);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", ...sx }}>
      {toolbar}
      <TableContainer ref={scrollRef} sx={{ flex: 1, overflow: "auto", scrollbarGutter: "stable" }}>
        <Table
          stickyHeader
          sx={{
            tableLayout: "fixed",
            borderCollapse: "separate",
            borderSpacing: 0,
            minWidth: minTableWidth,
          }}
        >
          <colgroup>
            {leafHeaders.map((header) => {
              const col = header.column;
              const meta = col.columnDef.meta as DataTableColumnMeta | undefined;
              const flex = meta?.flex;
              const isResized = col.id in columnSizing;
              const minW = col.columnDef.minSize;

              if (isResized) {
                const w = effectiveResizedColumnPixelWidth(col, columnSizing);
                return (
                  <col
                    key={col.id}
                    style={{
                      width: `${w}px`,
                      ...(minW != null ? { minWidth: `${minW}px` } : {}),
                    }}
                  />
                );
              }

              const width = flex != null && flex > 0 ? undefined : col.getSize();
              return (
                <col
                  key={col.id}
                  style={{
                    width: width ? `${width}px` : undefined,
                    minWidth: col.columnDef.minSize ? `${col.columnDef.minSize}px` : undefined,
                    maxWidth: col.columnDef.maxSize ? `${col.columnDef.maxSize}px` : undefined,
                  }}
                />
              );
            })}
          </colgroup>
          <DraggableTableHead
            table={table}
            headerHeight={headerHeight}
            columnSizing={columnSizing}
            hidingEnabled={enableColumnHiding}
            onHideColumn={onHideColumn}
          />
          {rows.length === 0 ? (
            <TableBody>
              <TableRow>
                <TableCell colSpan={leafHeaders.length} sx={{ textAlign: "center", py: 4 }}>
                  {noRowsContent}
                </TableCell>
              </TableRow>
            </TableBody>
          ) : virtualize ? (
            <VirtualizedBody
              rows={rows}
              rowHeight={rowHeight}
              onRowClick={onRowClick}
              onRowContextMenu={onRowContextMenu}
              getRowClassName={getRowClassName}
              getRowCursor={getRowCursor}
              scrollRef={scrollRef}
            />
          ) : (
            <PlainBody
              rows={rows}
              rowHeight={rowHeight}
              onRowClick={onRowClick}
              onRowContextMenu={onRowContextMenu}
              getRowClassName={getRowClassName}
              getRowCursor={getRowCursor}
            />
          )}
        </Table>
      </TableContainer>
      {pagination && rows.length > 0 && (
        <TablePagination
          component="div"
          count={table.getFilteredRowModel().rows.length}
          page={table.getState().pagination.pageIndex}
          rowsPerPage={table.getState().pagination.pageSize}
          onPageChange={(_e, page) => table.setPageIndex(page)}
          onRowsPerPageChange={(e) => table.setPageSize(Number(e.target.value))}
          rowsPerPageOptions={pageSizeOptions}
        />
      )}
    </Box>
  );
}
